import React, { useCallback, useEffect, useRef, useState } from 'react'
import Link from '@mui/material/Link'

type TruncatedTooltipLinkProps = {
  // The link text, also used as the tooltip content.
  text: string
  // Optional target. Omit for non-navigational items (e.g. the active page).
  href?: string
  // The (styled) link component to render as the visible, truncating link.
  component?: React.ElementType
  // Extra class names for the wrapper (e.g. 'active-item').
  className?: string
  // Extra class names for the link element itself.
  linkClassName?: string
  onClick?: () => void
}

/**
 * A link that truncates its text with a CSS ellipsis and shows a hover tooltip
 * with the full text, but only when the text is actually truncated.
 *
 * Truncation is detected by comparing scrollWidth to clientWidth on the link.
 * The tooltip element is rendered solely when overflow occurs, so non-overflowing
 * links never show a tooltip.
 */
const TruncatedTooltipLink = ({
  text,
  href,
  component: LinkComponent = Link,
  className,
  linkClassName,
  onClick,
}: TruncatedTooltipLinkProps) => {
  // Attach to the visible link so we can measure its rendered width.
  const ref = useRef<HTMLAnchorElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  // True when the full text is wider than the visible box (ellipsis is shown).
  const checkTruncation = useCallback(() => {
    const element = ref.current
    if (element) {
      setIsTruncated(element.scrollWidth > element.clientWidth)
    }
  }, [])

  // Re-check on mount, when the text changes, and on window resize.
  useEffect(() => {
    checkTruncation()
    window.addEventListener('resize', checkTruncation)
    return () => window.removeEventListener('resize', checkTruncation)
  }, [checkTruncation, text])

  const wrapperClassName = ['tooltip-parent', className].filter(Boolean).join(' ')

  return (
    <div className={wrapperClassName}>
      <LinkComponent
        ref={ref}
        href={href}
        onClick={onClick}
        className={linkClassName}
        aria-label={isTruncated ? text : undefined}
      >
        {text}
      </LinkComponent>
      {isTruncated ? (
        <span className="tooltip" aria-hidden="true">
          {text}
        </span>
      ) : null}
    </div>
  )
}

export default TruncatedTooltipLink
